import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import { Box, Checkbox, Stack, TextField, Typography } from '@mui/material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import Cancel from '@mui/icons-material/Cancel';
import { useDispatch, useSelector } from 'react-redux';
import { searchUser } from '../../redux/user/actions';
import _ from 'lodash';
import { IUserSummary } from '../../types/global/helper';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { userSearchSelector, searchingUserSearchSelector } from '../AvatarGroup/selector';
import { showToastMessage } from '../../utils/AppUtils';
import CustomButton from '../Button/CustomButton';

export interface IAvatarSelection {
    isEditMode?: boolean;
    title?: string;
    disableTitle?: boolean;
    placeholder?: string;
    value: IUserSummary[];
    onChange?: (selectedUsers: IUserSummary[]) => void;
    isSearchModalOpen?: boolean;
    singleSelection?: boolean;
    withProfileSearch?: boolean;
    strict?: boolean;
}

const PeopleSearch: React.FC<IAvatarSelection> = ({
    placeholder,
    value,
    onChange,
    singleSelection = false,
    withProfileSearch,
    strict,
}) => {
    const [selectedAvatar, setSelectedAvatar] = useState<IUserSummary[]>(value);
    const [initialSelection, setInitialSelection] = useState<IUserSummary[]>(value);
    const [searchValue, setSearchValue] = useState('');
    const [, setSearchModalOpen] = useState(false);
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const userResults = useSelector(userSearchSelector);
    const searchingUser = useSelector(searchingUserSearchSelector);

    const handleGetUsers = useCallback(async () => {
        dispatch(searchUser({ searchTerm: searchValue, withProfileSearch, strict }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useLayoutEffect(() => {
        // This here adds an additional re-render on the app . Check to see if you can move this to appLayout
        handleGetUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedAvatar) {
            setInitialSelection(selectedAvatar);
        }
    }, [selectedAvatar]);

    const handleModalClose = () => {
        setSearchModalOpen(false);
    };

    const handleSearch = useRef(
        _.debounce(searchTerm => {
            if (searchTerm.length > 3 && !searchingUser) {
                dispatch(searchUser({ searchTerm, withProfileSearch, strict }));
            }
        }, 1000),
    ).current;

    const handleSearchInputChange = (event: any) => {
        const searchValue = event.target.value;
        setSearchValue(searchValue);
        handleSearch(searchValue);
    };

    const handleCheckboxChange = (user: IUserSummary) => {
        const isChecked = initialSelection?.some(a => a._id === user._id);

        if (isChecked) {
            const initial = initialSelection.filter(u => u._id !== user._id);
            setInitialSelection(initial);
        } else {
            if (singleSelection) {
                // For single selection, replace the current selection
                setInitialSelection([user]);
            } else if (initialSelection.length < 9) {
                setInitialSelection(prevState => [...prevState, user]);
            } else {
                showToastMessage(`Maximum of 10 test users reached`);
            }
        }
    };

    const handleOnChooseTestUsers = () => {
        setSearchModalOpen(false);
        setSelectedAvatar(initialSelection);
        onChange && onChange(initialSelection);
        setInitialSelection([]);
        showToastMessage(`${initialSelection.length} ${initialSelection.length > 1 ? 'users' : 'user'} selected`);
    };

    return (
        <Stack>
            <TextField
                autoFocus
                variant="outlined"
                placeholder={placeholder ?? `Search and select test users...`}
                color="secondary"
                InputProps={{
                    placeholder: placeholder ?? `Search and select test users...`,
                    style: {
                        color: theme[themeStyle].lightWhite, // Change the placeholder color to white
                        border: `1px solid ${theme[themeStyle].lightWhite}`, // Set the border width and color
                        borderRadius: '50px', // Set the border radius
                        paddingLeft: '8px', // Add some padding for the text inside the input field
                        fontSize: 14,
                        minWidth: 200,
                    },
                }}
                inputProps={{
                    color: 'blue',
                }}
                // Add your search functionality here
                onChange={searchValue => {
                    handleSearchInputChange(searchValue);
                }}
                sx={{
                    py: 2,
                    pr: 0.1,
                    minWidth: '100%',
                }}
            />
            <Box sx={{ position: 'absolute', right: 0, top: 0 }}>
                <IconButton
                    color="primary"
                    onClick={handleModalClose}
                    style={{
                        borderRadius: '50%',
                        borderWidth: 1,
                        borderColor: theme[themeStyle].lightWhite,
                    }}>
                    <Cancel color="secondary" />
                </IconButton>
            </Box>
            <Box sx={{ mb: 3 }}>
                {userResults && userResults.length > 0 ? (
                    userResults.map((avatar, index) => (
                        <Stack flexDirection="row" alignItems="center" justifyContent="space-between" key={index}>
                            <Stack flexDirection="row" alignItems="center" mb={1.2} gap={1}>
                                <ThspianAvatar
                                    user={avatar}
                                    sx={{
                                        width: 50,
                                        height: 50,
                                        cursor: 'pointer',
                                    }}
                                />
                                <Typography color={{ color: theme[themeStyle].lightWhite }}>
                                    {avatar.firstName} {avatar.lastName}
                                </Typography>
                            </Stack>

                            <Checkbox
                                sx={{
                                    color: theme[themeStyle].highlight,
                                    '&.Mui-checked': {
                                        color: theme[themeStyle].highlight,
                                    },
                                }}
                                color="secondary"
                                checked={initialSelection.some(a => a._id === avatar._id)}
                                onChange={() => handleCheckboxChange(avatar as unknown as IUserSummary)}
                            />
                        </Stack>
                    ))
                ) : (
                    <Typography color="white" sx={{ textAlign: 'center', mt: 2 }}>
                        No user found
                    </Typography>
                )}
                <CustomButton
                    label="Confirm"
                    disabled={initialSelection.length < 1}
                    onClick={handleOnChooseTestUsers}
                    sx={{
                        position: 'absolute',
                        bottom: 5,
                        left: 10,
                        right: 10,
                        mb: 1,
                    }}
                />
            </Box>
        </Stack>
    );
};

export { PeopleSearch };
